import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useFlightFaresSource } from "@b2bportal/core-flights";
import {
  CallState,
  type FlightFareMeta,
  type FlightFaresSourceResult,
  type UseFlightFaresSource,
  type UseFlightFaresSourceProps,
} from "@b2bportal/core-types";
import { getPackageFarePrice } from "@/modules/packages/utilities/packageFarePrice";
import {
  PackageFlightSelectors,
  PackageSearchSelectors,
} from "@/store/packages/selectors";

/**
 * Custom flight fares source hook for packages that replaces flight prices
 * with package pricing from the packageStateByFareId.
 *
 * This hook wraps the base useFlightFaresSource and maps over the fares to
 * inject package prices, providing a cleaner data-source approach compared
 * to modifying prices at the component level.
 */
export const usePackageFlightFaresSource: UseFlightFaresSource = (
  props: UseFlightFaresSourceProps
): FlightFaresSourceResult => {
  const baseResult = useFlightFaresSource(props);

  const packageStateByFareId = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );

  const faresWithPackagePricing = useMemo(() => {
    if (baseResult.state !== CallState.Success || !baseResult.data) {
      return baseResult.data;
    }

    return baseResult.data.map((fareMeta: FlightFareMeta) => {
      const fareId = fareMeta.fareDetails.id;
      if (!fareId) return fareMeta;

      const packagePrice = getPackageFarePrice(
        packageStateByFareId?.[fareId],
        packageType
      );
      if (!packagePrice) return fareMeta;

      // Return a new fare meta with updated pricing
      // FlightCardFareList uses fareDetails.paxPricings[0].pricing.total for display
      // Only update the first paxPricing as packagePrice is already a total for all passengers
      return {
        ...fareMeta,
        fareDetails: {
          ...fareMeta.fareDetails,
          paxPricings: fareMeta.fareDetails.paxPricings?.map(
            (paxPricing, index) =>
              index === 0
                ? {
                    ...paxPricing,
                    pricing: {
                      ...paxPricing.pricing,
                      total: packagePrice,
                    },
                  }
                : paxPricing
          ),
        },
      };
    });
  }, [baseResult.state, baseResult.data, packageStateByFareId, packageType]);

  if (baseResult.state !== CallState.Success) {
    return baseResult;
  }

  return {
    state: CallState.Success,
    data: faresWithPackagePricing ?? [],
  };
};
